import { useState } from 'react';
import { Button, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { RateSelector } from './RateSelector';
import styles from './WizardFreightRateCard.module.css';
import { FreightCarrierDetailsModal } from '../../../freight-rate-card/freight-carrier-details-modal/FreightCarrierDetailsModal';
import { freightRateCardMessages } from '../../../freight-rate-card/freight-rate-card-messages';
import { getCarrierInitials } from '../../../freight-rate-card/get-carrier-initials';
import { getCarrierLogoSrc } from '../../../freight-rate-card/get-carrier-logo-src';

import type { LtlAddress, LtlQuote } from '../../../../types/ltl';
import type { ShipFromAddress } from '../../../../types/ship-from';
import type { CarrierRateRow } from '../../../freight-rate-card/freight-rate-card-types';

type WizardFreightRateCardProps = {
  carrierRow: CarrierRateRow;
  selectedOfferId?: string;
  onSelectRate: (quote: LtlQuote) => void;
  shipFromAddress: ShipFromAddress;
  destinationAddress: LtlAddress;
  zIndex?: number;
};

export const WizardFreightRateCard = ({
  carrierRow,
  selectedOfferId,
  onSelectRate,
  shipFromAddress,
  destinationAddress,
  zIndex,
}: WizardFreightRateCardProps) => {
  const intl = useIntl();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { carrierKey, carrierName, carrierScac, standard, guaranteed } = carrierRow;
  const logoSrc = getCarrierLogoSrc(carrierScac);

  const logo = logoSrc ? (
    <img alt={carrierName} className={styles.logoImage} src={logoSrc} />
  ) : (
    <span className={styles.logoInitials}>{getCarrierInitials(carrierName)}</span>
  );

  return (
    <div className={styles.card} data-testid={`freight-rate-card-${carrierKey}`}>
      <div className={styles.info}>
        <div className={styles.logoBox}>{logo}</div>
        <div className={styles.infoColumn}>
          <div className={styles.nameRow}>
            <Text className={styles.carrierName} fw={600} size='sm'>
              {carrierName}
            </Text>
            <Button classNames={{ label: styles.detailsLabel }} onClick={() => setIsDetailsOpen(true)} variant='hyperlink'>
              {intl.formatMessage(freightRateCardMessages.viewDetails)}
            </Button>
          </div>
          <div className={styles.selectors}>
            <RateSelector
              isSelected={!!standard && standard.offerId === selectedOfferId}
              label={intl.formatMessage(freightRateCardMessages.standardLabel)}
              onSelect={onSelectRate}
              quote={standard}
            />
            <RateSelector
              isSelected={!!guaranteed && guaranteed.offerId === selectedOfferId}
              label={intl.formatMessage(freightRateCardMessages.guaranteedLabel)}
              onSelect={onSelectRate}
              quote={guaranteed}
            />
          </div>
        </div>
      </div>
      <FreightCarrierDetailsModal
        carrierName={carrierName}
        carrierScac={carrierScac}
        destinationAddress={destinationAddress}
        onClose={() => setIsDetailsOpen(false)}
        opened={isDetailsOpen}
        quote={standard ?? guaranteed!}
        shipFromAddress={shipFromAddress}
        zIndex={zIndex}
      />
    </div>
  );
};
